<template>
  <div class="field">
    <!-- On the left: add new filter dropdown -->
    <!-- Visible only if there is at least one back-end sortable field -->
    <div
      v-if="columns && columns.filter((col) => col.backEndSearchable).length > 0"
      class="is-pulled-left"
    >
      <b-dropdown
        ref="columns-values-dropdown"
        aria-role="list"
        position="is-bottom-right"
        append-to-body
      >
        <template #trigger>
          <b-button icon-right="angle-down" class="button is-primary">
            {{ $t("table-filter.filter-columns") }}
          </b-button>
        </template>

        <b-dropdown-item aria-role="listitem" class="columns-filter-new" custom>
          <b-field :label="$t('table-filter.new-column-name')">
            <b-select
              v-model="newFilterField"
              expanded
              :placeholder="$t('table-filter.new-column-name-placeholder')"
            >
              <template
                v-for="column in columns.filter((col) => col.backEndSearchable)"
                :key="column.label"
              >
                <option :value="column.field">
                  {{ column.label }}
                </option>
              </template>
            </b-select>
          </b-field>

          <b-radio
            v-if="newFilterField.length > 0"
            v-model="newFilterMustBeNull"
            class="filter-value-radio"
            native-value="null"
          >
            <b-field :label="$t('table-filter.null')"></b-field>
          </b-radio>

          <b-radio
            v-if="newFilterField.length > 0 && newFilterField != 'dateModification'"
            v-model="newFilterMustBeNull"
            class="filter-value-radio"
            native-value="value"
          >
            <b-field :label="$t('table-filter.value-equals')"></b-field>
          </b-radio>
          <b-radio
            v-if="newFilterField.length > 0 && newFilterField == 'dateModification'"
            v-model="newFilterMustBeNull"
            class="filter-value-radio"
            native-value="value"
          >
            <b-field :label="$t('table-filter.value-equals-to-date')"></b-field>
          </b-radio>
          <b-input
            v-if="
              newFilterField.length > 0 &&
              !newFilterColumn.possibleSearchValues &&
              newFilterField != 'dateModification'
            "
            v-model="newFilterValue"
            :disabled="newFilterMustBeNull == 'null'"
            :placeholder="$t('table-filter.new-column-value-placeholder')"
          ></b-input>
          <DatePickerWithValidation
            v-if="
              newFilterField.length > 0 &&
              !newFilterColumn.possibleSearchValues &&
              newFilterField == 'dateModification'
            "
            v-model="newFilterValue"
            vid="dateModification"
            rules="required"
            :placeholder="$t('table-filter.value-equals-to-date')"
          />

          <b-select
            v-if="
              newFilterField.length > 0 &&
              newFilterColumn.possibleSearchValues &&
              newFilterColumn.possibleSearchValues.length > 0
            "
            v-model="newFilterValue"
            placeholder="table-filter.new-column-value-placeholder"
          >
            <template
              v-for="searchValue in newFilterColumn.possibleSearchValues"
              :key="searchValue"
            >
              <option :value="searchValue">
                {{ $t(searchValue) }}
              </option>
            </template>
          </b-select>
          <br />

          <b-button
            :disabled="
              newFilterField.length == 0 ||
              (newFilterMustBeNull == 'value' && newFilterValue.length == 0)
            "
            type="is-info"
            @click="addActiveFilter"
            >{{ $t("table-filter.add-filter") }}
          </b-button>
        </b-dropdown-item>
      </b-dropdown>

      <b-dropdown
        v-if="showExport"
        ref="columns-export-dropdown"
        aria-role="list"
        position="is-bottom-right"
        append-to-body
      >
        <template #trigger>
          <b-button icon-right="angle-down" class="button is-light">
            {{ $t("table-filter.export") }}
          </b-button>
        </template>

        <b-dropdown-item v-if="canExportCsv" aria-role="listitem" @click="exportCSV">
          {{ $t("table-filter.export-csv") }}
        </b-dropdown-item>
        <b-dropdown-item aria-role="listitem" @click="exportXLS">
          {{ exportXlsLabel }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="!canExportCsv && canExportXlsAll"
          aria-role="listitem"
          @click="exportXLSAll"
        >
          {{ $t("table-filter.export-xls-all") }}
        </b-dropdown-item>
      </b-dropdown>
      <slot></slot>
    </div>
    <!-- On the right: column filtering drowpdown -->
    <div v-if="showCustomColumns" class="is-pulled-right">
      <b-dropdown
        ref="columns-filter-dropdown"
        aria-role="list"
        position="is-bottom-left"
        append-to-body
        :close-on-click="true"
        :can-close="false"
      >
        <template #trigger>
          <b-button icon-right="angle-down" class="button is-primary">
            {{ $t("table-filter.customize-columns") }}
          </b-button>
        </template>

        <b-dropdown-item aria-role="listitem" class="columns-list" custom>
          <div class="columns-list-input">
            <b-field :label="$t('table-filter.customize-columns-label')" />
            <div class="inputNotice mb-2">
              {{ $t("table-filter.customize-columns-input-notice", [filteredColumns.length]) }}
            </div>
            <b-autocomplete
              v-model="columnSearch"
              open-on-focus
              :placeholder="$t('table-filter.customize-columns-placeholder')"
              :data="filteredColumns"
              icon="search"
              clearable
              clear-on-select
              @select="(option) => updateSelectedColumns(option, true)"
              @typing="(option) => getFilteredColumns(option)"
            />
            <div class="columns-list-tags">
              <div v-for="selectedColumn in selectedColumnNames" :key="selectedColumn">
                <div class="control">
                  <b-tag
                    type="is-light"
                    attached
                    :aria-close-label="$t('close-generic')"
                    closable
                    @close="updateSelectedColumns(selectedColumn, false)"
                  >
                    {{ selectedColumn }}
                  </b-tag>
                </div>
              </div>
            </div>
          </div>

          <b-button type="is-info" @click="updateVisibleColumns"
            >{{ $t("close-generic") }}
          </b-button>
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <br />
    <br />
    <!-- Below: list of active filters -->
    <b-field v-if="activeFilters.length > 0" grouped group-multiline>
      <div v-for="(filter, i) in activeFilters" :key="i" class="control">
        <b-taglist attached class="filters-list">
          <b-tag type="is-dark">{{ filter.label }}</b-tag>
          <b-tag type="is-grey">
            <span v-if="filter.values.length == 0">
              {{ $t("table-filter.none") }}
              <b-tag type="is-grey" class="close" @click="removeFilterValue(filter.property, '')">
                <b-icon icon="times" />
              </b-tag>
            </span>
            <span v-for="(value, j) in filter.values" :key="value">
              <span v-if="value.length > 1 && filter.property != 'dateModification'">
                {{ $t(value) }}
              </span>
              <span v-if="value.length > 1 && filter.property === 'dateModification'">
                {{ convertDateFormat(value) }}
              </span>
              <b-tag
                type="is-grey"
                class="close"
                @click="removeFilterValue(filter.property, value)"
              >
                <b-icon icon="times" />
              </b-tag>
              <span v-if="j < filter.values.length - 1"
                ><strong>{{ $t("table-filter.or") }}</strong></span
              >
            </span>
          </b-tag>
        </b-taglist>
      </div>
    </b-field>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import Column from "@/views/tables/Column"
import Activable from "@/views/utils/Activable"
import { FilterDTO } from "@/model/bean/GeneratedDTOs"
import { FilterDTOWithLabel } from "@/views/tables/FilterDTOWithLabel"
import DatePickerWithValidation from "../inputs/DatePickerWithValidation.vue"
import {
  BButton,
  BIcon,
  BDropdown,
  BDropdownItem,
  BInput,
  BSelect,
  BField,
  BRadio,
  BTag,
  BAutocomplete,
  BTaglist,
} from "buefy"

/**
 * Abstract class providing methods for sorting, paginating and handling s-tables.
 */
export default defineComponent({
  name: "TableFilterView",
  components: {
    BTaglist,
    BDropdownItem,
    DatePickerWithValidation,
    BButton,
    BInput,
    BField,
    BSelect,
    BDropdown,
    BRadio,
    BIcon,
    BTag,
    BAutocomplete,
  },
  props: {
    columns: {
      type: Array as PropType<Column[]>,
      default: () => [],
    },
    initialFilters: {
      type: Array as PropType<FilterDTO[]>,
      default: () => [],
    },
    page: {
      type: String,
      default: "",
    },
    showExport: {
      type: Boolean,
      default: true,
    },
    showCustomColumns: {
      type: Boolean,
      default: true,
    },
    canExportCsv: {
      type: Boolean,
      default: true,
    },
    canExportXlsAll: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    "update-visible-columns",
    "update-active-filters",
    "export-csv",
    "export-xls",
    "export-xls-all",
  ],
  data() {
    return {
      // List of selected column
      selectedColumnNames: new Array<string>(),
      // Filtered columns matching searched tags in column filtering search
      filteredColumns: new Array<string>(),
      // List of active filters
      activeFilters: new Array<FilterDTOWithLabel>(),
      newFilterField: "",
      newFilterColumn: new Column(),
      newFilterValue: "",
      newFilterMustBeNull: "value",
      columnSearch: "",
    }
  },
  computed: {
    exportXlsLabel(): string {
      const keyLabel = "table-filter.export-xls" + (this.page != "" ? "-format" : "")
      return this.$t(keyLabel).toString()
    },
  },
  watch: {
    newFilterField(): void {
      if (this.columns) {
        this.newFilterColumn = this.columns.filter((col) => col.field == this.newFilterField)[0]
      }
    },
  },
  mounted(): void {
    this.columns?.forEach((column) => {
      if (column.visible) {
        this.selectedColumnNames.push(column.label)
        this.getFilteredColumns("")
      }
    })
    const filtersCopy: FilterDTO[] = JSON.parse(JSON.stringify(this.initialFilters))
    filtersCopy.forEach((filter) => {
      const filterWithLabel = new FilterDTOWithLabel()
      filterWithLabel.property = filter.property
      const filtered = this.columns?.filter((col) => col.field == filter.property)
      if (filtered) {
        filterWithLabel.label = filtered.length ? filtered[0].label : ""
        filterWithLabel.values = filter.values
        this.activeFilters.push(filterWithLabel)
      }
    })
  },
  methods: {
    getFilteredColumns(search: string | number | null | undefined): void {
      this.filteredColumns = new Array<string>()
      const searchStr = search ? search.toString().toLowerCase() : ""
      this.columns?.forEach((column) => {
        if (
          column.canBeAdded &&
          this.selectedColumnNames.indexOf(column.label) == -1 &&
          column.label.toString().toLowerCase().indexOf(searchStr) >= 0
        ) {
          this.filteredColumns.push(column.label)
        }
      })
    },
    updateSelectedColumns(addedColumn: string, add: boolean): void {
      if (!addedColumn) {
        return
      }

      const indexColumn = this.selectedColumnNames.indexOf(addedColumn)

      if (add && indexColumn === -1) {
        this.selectedColumnNames.push(addedColumn)
      } else {
        this.selectedColumnNames.splice(indexColumn, 1)
      }
      this.columnSearch = ""
      this.getFilteredColumns("")
    },
    updateVisibleColumns(): void {
      const newVisibleColumns = new Array<Column>()
      this.columns.forEach((column) => {
        const newColumn = JSON.parse(JSON.stringify(column))
        newColumn.visible = this.selectedColumnNames.indexOf(column.label) != -1
        newVisibleColumns.push(newColumn)
      })

      // Close dropdown
      const dropdown = this.$refs["columns-filter-dropdown"] as unknown as Activable
      dropdown.isActive = false

      // Warn tableView that visible columns changed
      this.$emit("update-visible-columns", newVisibleColumns)
    },
    removeFilterValue(property: string, value: string): void {
      const filter = this.activeFilters.filter((filter) => filter.property == property)
      if (filter.length > 0) {
        if (filter[0].values.length > 1) {
          // Only remove value but none filter, other values remaine
          filter[0].values = filter[0].values.filter((val) => val != value)
        } else {
          // Was last value, remove filter from list
          this.activeFilters = this.activeFilters.filter((filter) => filter.property != property)
        }
      }

      // Warn tableView that filters changed
      this.$emit("update-active-filters", this.activeFilters)
    },
    addActiveFilter(): void {
      if (
        this.newFilterField.length > 0 &&
        (this.newFilterMustBeNull == "null" ||
          this.newFilterValue.length > 0 ||
          this.newFilterField == "dateModification")
      ) {
        // check if there is an already existing filter or create a new one
        const existingFilter = this.activeFilters.filter((filter) => {
          return filter.property == this.newFilterField
        })
        if (existingFilter.length > 0) {
          const filterValues = existingFilter[0].values

          // Push empty string if filter was without any value
          if (filterValues.length == 0) {
            filterValues.push("")
          }

          if (this.newFilterMustBeNull == "value") {
            //
            const item: any =
              this.newFilterField == "dateModification"
                ? new Date(this.newFilterValue).toISOString()
                : this.newFilterValue
            // Ajoute la valeur si pas déjà présente
            if (!filterValues.includes(item)) {
              filterValues.push(item)
            }
          } else {
            filterValues.push("")
          }
        } else {
          const newFilter = new FilterDTOWithLabel()
          newFilter.property = this.newFilterField
          newFilter.label = this.columns.filter((col) => col.field == this.newFilterField)[0].label
          if (this.newFilterMustBeNull == "value") {
            const item =
              this.newFilterField == "dateModification"
                ? new Date(this.newFilterValue).toISOString()
                : this.newFilterValue
            newFilter.values = [(item || "").trim()]
          } else {
            newFilter.values = []
          }
          this.activeFilters.push(newFilter)
        }

        // reset values and close dropdown
        this.newFilterField = ""
        this.newFilterValue = ""
        this.newFilterMustBeNull = "value"
        const dropdown = this.$refs["columns-values-dropdown"] as unknown as Activable
        dropdown.isActive = false

        // Warn tableView that filters changed
        this.$emit("update-active-filters", this.activeFilters)
      }
    },
    convertDateFormat(dateStr: any): string {
      // Assuming dateStr is in the format "mm/dd/yyyy hh:mm:ss"
      const dateParts = dateStr.split("T")[0].split("-")

      const day = dateParts[0]
      const month = dateParts[1]
      const year = dateParts[2]

      // Return in the format "jj/mm/aaaa"
      return `${day}/${month}/${year}`
    },
    exportCSV(): void {
      this.$emit("export-csv")
    },
    exportXLS(): void {
      this.$emit("export-xls")
    },
    exportXLSAll(): void {
      this.$emit("export-xls-all")
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

$filter-new-dropdown-width: 300px;
$filter-columns-filter-width: 500px;

.columns-list {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: $filter-columns-filter-width !important;
  padding: 10px;
}

.columns-list-input {
  width: $filter-columns-filter-width - 150px;
}

.columns-list-tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: 0.5rem;

  .control {
    margin-top: 0.5rem;
    margin-right: 0.5rem;
  }
}

.columns-filter-new {
  width: $filter-new-dropdown-width !important;
  padding: 10px;
}

.filters-list {
  height: 50px;

  .tag .close {
    margin-bottom: 0;
    vertical-align: middle;
  }
}

.close {
  padding-left: 0 !important;
  margin-left: 0 !important;
  padding-right: 3px !important;
  margin-right: 3px !important;
  cursor: pointer;

  :hover {
    color: $danger;
    font-weight: bolder;
  }
}

.filter-value-radio {
  padding-top: 5px;
  padding-bottom: 10px;
}
</style>
